import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Header from "./components/ui/Header";
import Footer from "./components/ui/Footer";
import GtmLoader from "./components/analytics/GtmLoader";
import SentryLoader from "./components/analytics/SentryLoader";
import AnalyticsRouteListener from "./components/analytics/AnalyticsRouteListener";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import ForBusinesses from "./pages/ForBusinesses";
import BusinessBookingSystemDemo from "./pages/BusinessBookingSystemDemo";
import BusinessBookingSiteDemo from "./pages/BusinessBookingSiteDemo";
import ApartmentManagersCleaningPartners from "./pages/ApartmentManagersCleaningPartners";
import Auth from "./pages/Auth";
import AuthReset from "./pages/AuthReset";
import AuthActivate from "./pages/AuthActivate";
import CompanyDetail from "./pages/CompanyDetail";
import BookingForm from "./pages/BookingForm";
import BookingSummary from "./pages/BookingSummary";
import PaymentSuccess from "./pages/PaymentSuccess";
import MyOrders from "./pages/MyOrders";
import Profile from "./pages/Profile";
import Dashboard from "./pages/Dashboard";
import SupportCenter from "./pages/SupportCenter";
import BookingDetail from "./pages/BookingDetail";
import CompanyDashboard from "./pages/company/CompanyDashboard";
import CompanyOnboarding from "./pages/company/CompanyOnboarding";
import CompanyProfile from "./pages/company/CompanyProfile";
import VendorOnboardingRedirect from "./pages/VendorOnboardingRedirect";
import BulkBookingImport from "./pages/BulkBookingImport";
import Terms from "./pages/legal/Terms";
import Privacy from "./pages/legal/Privacy";
import Cookies from "./pages/legal/Cookies";
import AcceptableUse from "./pages/legal/AcceptableUse";
import Imprint from "./pages/legal/Imprint";
import Subprocessors from "./pages/legal/Subprocessors";
import RefundPolicy from "./pages/legal/RefundPolicy";
import VendorAgreement from "./pages/legal/VendorAgreement";
import DataProcessingAddendum from "./pages/legal/DataProcessingAddendum";
import Status from "./pages/Status";
import FAQ from "./pages/FAQ";
import LandingServizio from "./pages/LandingServizio";
import LandingAzienda from "./pages/LandingAzienda";
// removed standalone Analytics page

const queryClient = new QueryClient();

function ScrollToTop() {
  const { pathname, search, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const id = window.decodeURIComponent(hash.slice(1));
      window.requestAnimationFrame(() => {
        document.getElementById(id)?.scrollIntoView({ block: "start" });
      });
      return;
    }

    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname, search, hash]);

  return null;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <GtmLoader />
      <SentryLoader />
      <BrowserRouter>
        <AnalyticsRouteListener />
        <ScrollToTop />
        <div className="app-shell min-h-screen bg-background">
          <Header />
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/for-businesses" element={<ForBusinesses />} />
            <Route path="/for-businesses/booking-system-demo" element={<BusinessBookingSystemDemo />} />
            <Route path="/for-businesses/booking-system-demo/:style" element={<BusinessBookingSiteDemo />} />
            <Route path="/for-businesses/booking-system-demo/:style/:page" element={<BusinessBookingSiteDemo />} />
            <Route path="/apartment-manager/partner-pulizie" element={<ApartmentManagersCleaningPartners />} />
            <Route path="/servizi/:servizio/:città" element={<LandingServizio />} />
            <Route path="/aziende/pulizie/:città" element={<LandingAzienda />} />
            <Route path="/servizi/:service/:city" element={<LandingServizio />} />
            <Route path="/aziende/pulizie/:city" element={<LandingAzienda />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/auth/reset" element={<AuthReset />} />
            <Route path="/auth/activate" element={<AuthActivate />} />
            <Route path="/company/:id" element={<CompanyDetail />} />
            <Route path="/c/:slug" element={<CompanyDetail />} />
            <Route path="/booking/:companyId/:serviceId" element={<BookingForm />} />
            <Route path="/booking/bulk/:companyId/:serviceId" element={<BulkBookingImport />} />
            <Route path="/booking/:bookingId" element={<BookingDetail />} />
            <Route path="/booking-summary/:companyId/:serviceId" element={<BookingSummary />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            {/* /my-orders removed; use /profile */}
            <Route path="/profile" element={<Profile />} />
            <Route path="/support" element={<SupportCenter />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/bulk-order" element={<BulkBookingImport />} />
            {false && <Route path="/analytics" element={null} />}
            
            {/* Company (Vendor) Routes */}
            <Route path="/company/dashboard" element={<CompanyDashboard />} />
            <Route path="/company/onboarding" element={<CompanyOnboarding />} />
            <Route path="/company/profile" element={<CompanyProfile />} />
            <Route path="/vendor/onboarding" element={<VendorOnboardingRedirect />} />

            {/* Legal */}
            <Route path="/legal/terms" element={<Terms />} />
            <Route path="/legal/privacy" element={<Privacy />} />
            <Route path="/legal/cookies" element={<Cookies />} />
            <Route path="/legal/aup" element={<AcceptableUse />} />
            <Route path="/legal/imprint" element={<Imprint />} />
            <Route path="/legal/subprocessors" element={<Subprocessors />} />
            <Route path="/legal/refunds" element={<RefundPolicy />} />
            <Route path="/legal/vendor-agreement" element={<VendorAgreement />} />
            <Route path="/legal/dpa" element={<DataProcessingAddendum />} />

            {/* Status */}
            <Route path="/status" element={<Status />} />
            <Route path="/faq" element={<FAQ />} />
            
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          <Footer />
        </div>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
